import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';

import { getCookieConsent, setCookieConsent } from '../../utils/cookieUtils';
import THEME from '../../constants/theme';

const CookieConsent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice using proper cookies
    const hasConsent = getCookieConsent();
    if (!hasConsent) {
      // Small delay to ensure page has loaded
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences - getCookieConsent already returns parsed object
      setPreferences(hasConsent);
      updateGTMConsent(hasConsent);
    }
  }, []);

  const updateGTMConsent = prefs => {
    // Update Google Tag Manager consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        functionality_storage: prefs.functional ? 'granted' : 'denied'
      });
    }

    // Alternative for dataLayer if gtag is not available
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_update',
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        functionality_storage: prefs.functional ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    setCookieConsent(allAccepted);
    updateGTMConsent(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyNecessary);
    setCookieConsent(onlyNecessary);
    updateGTMConsent(onlyNecessary);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    setCookieConsent(preferences);
    updateGTMConsent(preferences);
    setShowPreferences(false);
    setShowBanner(false);
  };

  const handlePreferenceChange = category => event => {
    if (category === 'necessary') return; // Cannot be disabled

    setPreferences(prev => ({
      ...prev,
      [category]: event.target.checked
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
          p: { xs: 2, md: 3 },
          zIndex: 9999,
          borderTop: `3px solid ${THEME.COLORS.PRIMARY_PURPLE}`
        }}
      >
        <Box
          sx={{
            maxWidth: '1200px',
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 2, md: 3 }
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isMobile ? 'body2' : 'body1'}
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                color: THEME.COLORS.NEUTRAL_BLACK
              }}
            >
              We use cookies to enhance your experience
            </Typography>
            <Typography
              variant={isMobile ? 'caption' : 'body2'}
              sx={{ 
                lineHeight: 1.4,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_BLACK,
                opacity: 0.8
              }}
            >
              We use essential cookies to make our website work and analytics
              cookies to understand how you interact with our website. By
              clicking "Accept All", you consent to the use of all cookies. You
              can customize your preferences or learn more in our{' '}
              <Box
                component='a'
                href='/legal/cookie-policy'
                sx={{
                  color: THEME.COLORS.PRIMARY_PURPLE,
                  textDecoration: 'underline',
                  '&:hover': { textDecoration: 'none' }
                }}
              >
                Cookie Policy
              </Box>
              .
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              alignItems: 'stretch',
              minWidth: { xs: 'auto', md: '400px' }
            }}
          >
            <Button
              variant='outlined'
              onClick={() => setShowPreferences(true)}
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                whiteSpace: 'nowrap',
                borderColor: THEME.COLORS.PRIMARY_PURPLE,
                color: THEME.COLORS.PRIMARY_PURPLE,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                '&:hover': {
                  borderColor: THEME.COLORS.PRIMARY_PURPLE_DARK,
                  backgroundColor: `${THEME.COLORS.PRIMARY_PURPLE}10`
                }
              }}
            >
              Customize
            </Button>
            <Button
              variant='outlined'
              onClick={handleRejectAll}
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                whiteSpace: 'nowrap',
                borderColor: THEME.COLORS.NEUTRAL_GRAY,
                color: THEME.COLORS.NEUTRAL_BLACK,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                '&:hover': {
                  borderColor: THEME.COLORS.NEUTRAL_GRAY_DARK,
                  backgroundColor: `${THEME.COLORS.NEUTRAL_GRAY}10`
                }
              }}
            >
              Reject All
            </Button>
            <Button
              variant='contained'
              onClick={handleAcceptAll}
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                whiteSpace: 'nowrap',
                backgroundColor: THEME.COLORS.PRIMARY_PURPLE,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                '&:hover': {
                  backgroundColor: THEME.COLORS.PRIMARY_PURPLE_DARK
                }
              }}
            >
              Accept All
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Cookie Preferences Dialog */}
      <Dialog
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            maxHeight: '90vh',
            borderRadius: THEME.BORDERS.RADIUS.MEDIUM
          }
        }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography 
            variant='h6' 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              color: THEME.COLORS.NEUTRAL_BLACK
            }}
          >
            Cookie Preferences
          </Typography>

          <Typography
            variant='body2'
            sx={{ 
              mb: 3, 
              lineHeight: 1.5,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              color: THEME.COLORS.NEUTRAL_BLACK,
              opacity: 0.8
            }}
          >
            We use different types of cookies to optimize your experience on our
            website. You can choose which categories you'd like to allow. Please
            note that blocking some types of cookies may impact your experience
            on our website and the services we are able to offer.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Necessary Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.necessary}
                    disabled
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME.COLORS.PRIMARY_PURPLE
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME.COLORS.PRIMARY_PURPLE
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography 
                      variant='subtitle1' 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                    >
                      Necessary Cookies
                    </Typography>
                    <Typography 
                      variant='body2' 
                      sx={{
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                        color: THEME.COLORS.NEUTRAL_BLACK,
                        opacity: 0.7
                      }}
                    >
                      These cookies are essential for the website to function
                      properly. They cannot be disabled.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider />

            {/* Analytics Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.analytics}
                    onChange={handlePreferenceChange('analytics')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME.COLORS.PRIMARY_PURPLE
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME.COLORS.PRIMARY_PURPLE
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography 
                      variant='subtitle1' 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                    >
                      Analytics Cookies
                    </Typography>
                    <Typography 
                      variant='body2' 
                      sx={{
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                        color: THEME.COLORS.NEUTRAL_BLACK,
                        opacity: 0.7
                      }}
                    >
                      These cookies help us understand how visitors interact
                      with our website by collecting and reporting information
                      anonymously.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider />

            {/* Marketing Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.marketing}
                    onChange={handlePreferenceChange('marketing')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME.COLORS.PRIMARY_PURPLE
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME.COLORS.PRIMARY_PURPLE
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography 
                      variant='subtitle1' 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                    >
                      Marketing Cookies
                    </Typography>
                    <Typography 
                      variant='body2' 
                      sx={{
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                        color: THEME.COLORS.NEUTRAL_BLACK,
                        opacity: 0.7
                      }}
                    >
                      These cookies are used to track visitors across websites
                      to display relevant and engaging advertisements.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider />

            {/* Functional Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.functional}
                    onChange={handlePreferenceChange('functional')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME.COLORS.PRIMARY_PURPLE
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME.COLORS.PRIMARY_PURPLE
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography 
                      variant='subtitle1' 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                    >
                      Functional Cookies
                    </Typography>
                    <Typography 
                      variant='body2' 
                      sx={{
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                        color: THEME.COLORS.NEUTRAL_BLACK,
                        opacity: 0.7
                      }}
                    >
                      These cookies enable enhanced functionality and
                      personalization such as videos and live chats.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setShowPreferences(false)} 
            sx={{
              color: THEME.COLORS.NEUTRAL_BLACK,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSavePreferences} 
            variant='contained'
            sx={{
              backgroundColor: THEME.COLORS.PRIMARY_PURPLE,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
              '&:hover': {
                backgroundColor: THEME.COLORS.PRIMARY_PURPLE_DARK
              }
            }}
          >
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
